import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAdminFilters } from "@/contexts/AdminFiltersContext";
import {
  Users, AtSign, Mail, MailOpen, Archive, Clock, Shield,
  TrendingUp, ArrowRight, Zap, UserPlus, Tag,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AdminPricing } from "@/components/AdminPricing";
import { AdminExperiments } from "@/components/AdminExperiments";

export default function AdminPage({ tab }) {
  const { hideTestData } = useAdminFilters();
  const emQ = hideTestData ? "&exclude_em=1" : "";
  const emQFirst = hideTestData ? "?exclude_em=1" : "";
  const [activeTab, setActiveTab] = useState(tab || "overview");
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funnelDays, setFunnelDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, logsRes, usersRes] = await Promise.all([
        api.get(`/admin/stats${emQFirst}`),
        api.get(`/admin/audit-logs?limit=20${emQ}`),
        api.get(`/admin/users${emQFirst}`),
      ]);
      setStats(statsRes.data);
      setAuditLogs(logsRes.data.logs);
      setUsers(usersRes.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [emQ, emQFirst]);

  const fetchFunnel = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/conversion-funnel?days=${funnelDays}${emQ}`);
      setFunnel(data);
    } catch {
      // silently fail - funnel is optional
    }
  }, [funnelDays, emQ]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: "Users", value: stats?.users || 0, icon: Users, color: "bg-blue-50 text-blue-600" },
    { label: "Aliases", value: stats?.aliases || 0, icon: AtSign, color: "bg-green-50 text-green-600" },
    { label: "Total Emails", value: stats?.total_emails || 0, icon: Mail, color: "bg-amber-50 text-amber-600" },
    { label: "Unread", value: stats?.unread_emails || 0, icon: MailOpen, color: "bg-red-50 text-red-600" },
    { label: "Archived", value: stats?.archived_emails || 0, icon: Archive, color: "bg-[#F5F5F5] text-[#888888]" },
  ];

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "pricing", label: "Promotions" },
    { id: "experiments", label: "Experiments" },
    { id: "funnel", label: "Conversion Funnel" },
    { id: "logs", label: "Audit Logs" },
  ];

  return (
    <ScrollArea className="h-full">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6 page-enter" data-testid="admin-page">
        {/* Header + Tabs only when no tab prop (standalone mode) */}
        {!tab && (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Shield size={18} className="text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-medium text-[#111111]" data-testid="admin-title">Admin Dashboard</h2>
                <p className="text-sm text-[#888888]">Tenant overview, conversion funnel, and audit logs</p>
              </div>
            </div>

            <div className="flex gap-1 border-b border-[#E5E5E5]" data-testid="admin-tabs">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === t.id
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-[#888888] hover:text-[#555555]"
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-stats">
              {statCards.map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="p-4 rounded-lg border border-[#E5E5E5] bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${color}`}>
                    <Icon size={16} />
                  </div>
                  <p className="text-2xl font-heading font-medium text-[#111111]">{value}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            <Separator />

            {/* Users Table */}
            <div data-testid="admin-users-section">
              <h3 className="font-heading text-lg font-medium text-[#111111] mb-3">Users</h3>
              <div className="border border-[#E5E5E5] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Name</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Email</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Auth</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Role</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {users.map((u, i) => (
                      <tr key={i} className="hover:bg-[#F7F7F7]" data-testid={`user-row-${i}`}>
                        <td className="px-4 py-2.5 text-[#111111] font-medium">{u.name}</td>
                        <td className="px-4 py-2.5 text-[#111111] text-xs">{u.email}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className="text-[10px] font-mono">
                            {u.auth_provider === "google" ? "Google" : u.auth_provider === "both" ? "Both" : "Email"}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className="text-[10px] font-mono capitalize">{u.role}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={`text-[10px] ${u.is_active !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                            {u.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div data-testid="pricing-tab">
            <AdminPricing />
          </div>
        )}

        {/* Experiments Tab */}
        {activeTab === "experiments" && (
          <div data-testid="experiments-tab">
            <AdminExperiments />
          </div>
        )}

        {/* Conversion Funnel Tab */}
        {activeTab === "funnel" && (
          <div className="space-y-6" data-testid="conversion-funnel-tab">
            {/* Period Selector */}
            <div className="flex items-center gap-2">
              {[7, 30, 90].map((d) => (
                <Button
                  key={d}
                  variant={funnelDays === d ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFunnelDays(d)}
                  className={funnelDays === d ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`funnel-period-${d}`}
                >
                  {d}d
                </Button>
              ))}
            </div>

            {funnel ? (
              <>
                {/* Funnel Visualization */}
                <div className="space-y-3" data-testid="funnel-visualization">
                  <h3 className="font-heading text-lg font-medium text-[#111111]">Conversion Funnel</h3>
                  <FunnelSteps funnel={funnel.funnel} />
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="funnel-metrics">
                  <MetricCard
                    label="Free Created"
                    value={funnel.funnel.free_aliases_created}
                    period={funnel.funnel.free_created_period}
                    icon={Zap}
                    color="bg-blue-50 text-blue-600"
                  />
                  <MetricCard
                    label="Claimed"
                    value={funnel.funnel.claimed}
                    period={funnel.funnel.claimed_period}
                    icon={AtSign}
                    color="bg-amber-50 text-amber-600"
                  />
                  <MetricCard
                    label="Registered"
                    value={funnel.funnel.registered_users}
                    period={funnel.funnel.registered_period}
                    icon={UserPlus}
                    color="bg-green-50 text-green-600"
                  />
                  <MetricCard
                    label="Conversion Rate"
                    value={`${funnel.funnel.conversion_rate}%`}
                    icon={TrendingUp}
                    color="bg-purple-50 text-purple-600"
                    highlight
                  />
                </div>

                <Separator />

                {/* Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="funnel-breakdown">
                  <div className="p-4 rounded-lg border border-[#E5E5E5] bg-white space-y-3">
                    <h4 className="text-sm font-medium text-[#111111]">Claim Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Auto-claimed (at register)</span>
                        <span className="font-mono font-medium text-[#111111]">{funnel.breakdown.auto_claimed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Manual claimed</span>
                        <span className="font-mono font-medium text-[#111111]">{funnel.breakdown.manual_claimed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Emails migrated</span>
                        <span className="font-mono font-medium text-[#111111]">{funnel.breakdown.total_emails_migrated}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border border-[#E5E5E5] bg-white space-y-3">
                    <h4 className="text-sm font-medium text-[#111111]">Auth & Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Google users</span>
                        <span className="font-mono font-medium text-[#111111]">{funnel.breakdown.google_users}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Email-only users</span>
                        <span className="font-mono font-medium text-[#111111]">{funnel.breakdown.email_only_users}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Active free aliases</span>
                        <span className="font-mono font-medium text-green-600">{funnel.breakdown.active_free_aliases}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#888888]">Expired free aliases</span>
                        <span className="font-mono font-medium text-red-600">{funnel.breakdown.expired_free_aliases}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Conversions */}
                {funnel.recent_conversions?.length > 0 && (
                  <>
                    <Separator />
                    <div data-testid="recent-conversions">
                      <h3 className="font-heading text-lg font-medium text-[#111111] mb-3">Recent Conversions</h3>
                      <div className="space-y-2">
                        {funnel.recent_conversions.map((c, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]" data-testid={`conversion-${i}`}>
                            <div className="w-7 h-7 rounded-md bg-green-100 flex items-center justify-center shrink-0">
                              <TrendingUp size={13} className="text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-xs font-mono text-[#555555]">{c.alias_id?.substring(0, 8)}...</code>
                                <Badge variant="outline" className="text-[10px] font-mono">
                                  {c.auto_claimed ? "Auto" : "Manual"}
                                </Badge>
                                <span className="text-[10px] font-mono text-[#888888]">{c.emails_migrated} emails</span>
                              </div>
                              <p className="text-[11px] text-[#888888] mt-0.5">
                                {c.converted_at ? formatDistanceToNow(new Date(c.converted_at), { addSuffix: true }) : ""}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-sm text-[#888888]">Loading funnel data...</div>
            )}
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === "logs" && (
          <div data-testid="admin-audit-section">
            <h3 className="font-heading text-lg font-medium text-[#111111] mb-3">Audit Logs</h3>
            <div className="space-y-2">
              {auditLogs.length === 0 ? (
                <p className="text-sm text-[#888888] py-6 text-center">No audit logs yet</p>
              ) : (
                auditLogs.map((log, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]" data-testid={`audit-log-${i}`}>
                    <div className="w-7 h-7 rounded-md bg-zinc-200 flex items-center justify-center shrink-0 mt-0.5">
                      <Clock size={13} className="text-[#888888]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] font-mono">{log.event_type}</Badge>
                        {log.ip && <span className="text-[10px] font-mono text-[#888888]">{log.ip}</span>}
                      </div>
                      <p className="text-[11px] text-[#888888] mt-1">
                        {log.timestamp ? formatDistanceToNow(new Date(log.timestamp), { addSuffix: true }) : ""}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function FunnelSteps({ funnel }) {
  const steps = [
    { label: "Created", value: funnel.free_aliases_created, color: "bg-blue-600" },
    { label: "Claimed", value: funnel.claimed, color: "bg-amber-500" },
    { label: "Registered", value: funnel.registered_users, color: "bg-green-500" },
  ];

  const maxVal = Math.max(...steps.map((s) => s.value), 1);

  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const width = Math.max((step.value / maxVal) * 100, 8);
        const rate = i > 0 && steps[i - 1].value > 0
          ? Math.round((step.value / steps[i - 1].value) * 100)
          : null;

        return (
          <div key={step.label} className="flex items-center gap-3">
            <div className="w-20 shrink-0 text-right">
              <span className="text-xs font-mono text-[#888888]">{step.label}</span>
            </div>
            <div className="flex-1 h-8 bg-[#F5F5F5] rounded-md overflow-hidden relative">
              <div
                className={`h-full ${step.color} rounded-md transition-all duration-500 flex items-center justify-end pr-2`}
                style={{ width: `${width}%` }}
              >
                <span className="text-xs font-mono font-medium text-white">{step.value}</span>
              </div>
            </div>
            {rate !== null && (
              <div className="w-12 shrink-0 flex items-center gap-0.5">
                <ArrowRight size={10} className="text-[#888888]" />
                <span className="text-xs font-mono text-[#888888]">{rate}%</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MetricCard({ label, value, period, icon: Icon, color, highlight }) {
  return (
    <div className={`p-4 rounded-lg border bg-white ${highlight ? "border-purple-500/20" : "border-[#E5E5E5]"}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${color}`}>
        <Icon size={16} />
      </div>
      <p className={`text-2xl font-heading font-medium ${highlight ? "text-purple-700" : "text-[#111111]"}`}>{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mt-0.5">{label}</p>
      {period !== undefined && (
        <p className="text-[10px] font-mono text-[#888888] mt-1">+{period} this period</p>
      )}
    </div>
  );
}
